import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Crown, Tv } from "lucide-react";
import api from "@/lib/api";

const REFRESH_MS = 5 * 60 * 1000; // poll every 5 minutes

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "just now";
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}w ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

export default function RecentSupporters() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        const { data } = await api.get("/donors/recent", { params: { limit: 6 } });
        if (!cancel) setItems(data.items || []);
      } catch {
        if (!cancel) setItems([]);
      }
    };
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { cancel = true; clearInterval(t); };
  }, []);

  // While loading, occupy space but show nothing flashy
  if (items === null) {
    return (
      <div
        className="rounded-xl border border-[var(--tt-border)] bg-[var(--tt-surface)]/60 p-3 mb-4"
        data-testid="recent-supporters-loading"
      >
        <div className="h-3 w-24 bg-white/10 rounded animate-pulse mb-3" />
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-7 bg-white/5 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border border-[var(--tt-border)] bg-gradient-to-b from-[var(--tt-primary)]/5 to-transparent p-3 mb-4"
      data-testid="recent-supporters"
    >
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-1.5">
          <Heart className="w-3 h-3 text-[var(--tt-primary)] fill-[var(--tt-primary)]" />
          <h3 className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--tt-text-faint)]">
            Recent Backers
          </h3>
        </div>
        <button
          onClick={() => navigate("/downloads")}
          className="text-[9px] font-bold uppercase tracking-wider text-[var(--tt-primary)] hover:text-[var(--tt-primary-strong)] transition-colors"
          data-testid="recent-supporters-cta"
        >
          Join
        </button>
      </div>

      {items.length === 0 ? (
        <button
          onClick={() => navigate("/downloads")}
          data-testid="recent-supporters-empty"
          className="w-full text-left rounded-lg p-2.5 bg-[var(--tt-surface-2)] hover:bg-[var(--tt-surface-2)]/60 border border-dashed border-[var(--tt-primary)]/30 transition-colors"
        >
          <div className="text-xs font-semibold text-white leading-tight">
            Be the first to back TorHub
          </div>
          <div className="text-[10px] text-[var(--tt-text-muted)] mt-1">
            From £5/mo · keeps the cloud running
          </div>
        </button>
      ) : (
        <ul className="space-y-1">
          {items.map((s, i) => {
            const isPremium = s.tier === "premium";
            const TierIcon = isPremium ? Crown : Tv;
            return (
              <li
                key={`${s.first_name}-${i}`}
                data-testid={`recent-supporter-${i}`}
                className="flex items-center gap-2 px-1.5 py-1 rounded-md hover:bg-white/5 transition-colors"
              >
                <div
                  className="w-6 h-6 rounded-full flex-none flex items-center justify-center text-[10px] font-extrabold uppercase"
                  style={{
                    background: isPremium
                      ? "linear-gradient(135deg,#FFD700 0%,#FF8B00 100%)"
                      : "linear-gradient(135deg,#00A8E1 0%,#9D4CDD 100%)",
                    color: "#000",
                  }}
                  title={isPremium ? "£10 Premium" : "£5 TV"}
                >
                  {s.avatar ? (
                    <img src={s.avatar} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    s.first_name.slice(0, 1)
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-white truncate leading-tight">
                    {s.first_name}
                    <TierIcon
                      className="inline-block ml-1 w-3 h-3 align-[-2px]"
                      style={{ color: isPremium ? "#FFD700" : "#00A8E1" }}
                    />
                  </div>
                  <div className="text-[10px] text-[var(--tt-text-faint)] leading-tight font-mono-tt">
                    £{Math.round((s.amount || 0))} · {timeAgo(s.at)}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
